import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type JSX,
  type ReactNode,
} from "react";

import { RadioField } from "@vega-core/components/Form/Controls/Radio/RadioField/RadioField";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { useTouchFieldOnSubmit } from "@vega-core/helpers/formik.helper";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  disabled?: boolean;
  error?: string;
  inputStyle?: string;
  isInlineList?: boolean;
  isOptional?: boolean;
  items: RadioItemProps[];
  labelVariant?: "compact" | "default" | "transparent";
  label?: ReactNode | string;
  labelHint?: ReactNode | string | null;
  labelTag?: ReactNode;
  name: string;
  onBlur?: FocusEventHandler;
  onChange?: ChangeEventHandler;
  setChange?: (field: string, value: boolean) => void;
  showDescriptions?: boolean;
  title?: ReactNode | string;
  value?: string;
  tooltipContentClassname?: string;
  fullWidth?: boolean;
};

export type RadioItemProps = {
  description?: JSX.Element | string;
  content?: ReactNode;
  title: JSX.Element | string;
  value: string;
  tooltip?: {
    title: string;
    content: string;
  };
};

export function RadioGroup({
  disabled = false,
  error,
  inputStyle,
  isInlineList,
  setChange,
  isOptional,
  labelVariant,
  items,
  name,
  onBlur,
  showDescriptions,
  title,
  tooltipContentClassname,
  value,
  labelHint,
  labelTag,
  fullWidth,
}: Props) {
  const selectedItem = items.find((item) => value === item.value);

  useTouchFieldOnSubmit(name);

  return (
    <div className="space-y-3">
      {title ? (
        <FormLabel
          forName={name}
          isOptional={isOptional}
          label={title}
          labelHint={labelHint}
          tag={labelTag}
        />
      ) : null}
      <fieldset
        className={cnTwMerge({
          "noSelect my-1.5 space-y-2": isTruthy(title),
          "sm:flex sm:items-center sm:space-x-10 sm:space-y-0":
            isTruthy(isInlineList),
          "cursor-not-allowed": disabled,
        })}
        disabled={disabled}
      >
        {items.map((item) => (
          <div className="space-y-2" key={item.value}>
            <RadioField
              checked={value === item.value}
              content={item.content}
              description={item.description}
              disabled={disabled}
              fullWidth={fullWidth}
              inputStyle={inputStyle ?? ""}
              isInlineList={isInlineList}
              labelVariant={labelVariant}
              name={name}
              onBlur={onBlur}
              setChange={setChange}
              title={item.title}
              tooltip={item.tooltip}
              tooltipContentClassname={tooltipContentClassname}
              value={item.value}
              visibleDescription={showDescriptions}
            />
          </div>
        ))}
      </fieldset>
      {isInlineList && selectedItem?.content ? (
        <div className="ml-4">{selectedItem.content}</div>
      ) : null}
      {isTruthy(error) ? (
        <ValidationErrorText dataTestId={error}>{error}</ValidationErrorText>
      ) : null}
    </div>
  );
}
